import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import {
  Users, Search, Plus, Building2, Mail,
  Loader2, AlertCircle,
} from 'lucide-react'
import UserAvatar from '@/components/UserAvatar'
import PageHeader from '@/components/PageHeader'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { useToast } from '@/hooks/use-toast'
import { apiService } from '@/services/api'
import { formatDate } from '@/lib/utils'

// ─── Supporting Components ────────────────────────────────────────────────────

const resolveRoleName = (client) => {
  const role = (Array.isArray(client?.roles) && client.roles[0]) || client?.role
  if (role && typeof role === 'object') return role.display_name || role.name || 'User'
  if (typeof role === 'string') return role
  return 'User'
}

const RoleBadge = ({ client }) => {
  const roleName = resolveRoleName(client)
  const styles = {
    'Company Admin': 'bg-orange-50 text-orange-700 border border-orange-100',
    Admin:      'bg-red-50 text-red-700 border border-red-100',
    Consultant: 'bg-blue-50 text-blue-700 border border-blue-100',
    Trainer:    'bg-blue-50 text-blue-700 border border-blue-100',
    Client:     'bg-gray-50 text-gray-600 border border-gray-200',
  }
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${styles[roleName] || styles.Client}`}>
      {roleName}
    </span>
  )
}

const StatusBadge = ({ active }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md ${
    active
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
      : 'bg-red-50 text-red-600 border border-red-100'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-red-400'}`} />
    {active ? 'Active' : 'Inactive'}
  </span>
)

// ─── Add User Form Helpers ─────────────────────────────────────────────────────

const selectFieldClass = 'h-11 w-full rounded-xl border border-input bg-background px-3.5 text-sm text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60'

const FormField = ({ label, required, hint, className = '', children }) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="block text-sm font-medium text-slate-700">
      {label}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
  </div>
)

const formatDisplayDate = (value) => formatDate(value, { fallback: 'Not set' })

const getUserName = (client) => {
  const parts = [client.firstname, client.middlename, client.lastname].filter(Boolean)
  return parts.length ? parts.join(' ') : client.email || 'Unnamed User'
}

const getMobile = (client) => {
  return client.personal?.mobilephone || client.mobilephone || client.mobile || 'Not set'
}

const getLocation = (client) => {
  const city = client.personal?.city || client.city
  const state = client.personal?.state || client.state
  if (city && state) return `${city}, ${state}`
  return city || state || 'Not set'
}

const getBirthDate = (client) => {
  return formatDisplayDate(client.personal?.birthdate || client.birthdate)
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const CompanyClients = () => {
  const { companyUrl, companyDetails, user } = useAuthStore()
  const { states, fetchStates, isLoadingStates } = useUserStore()
  const { toast } = useToast()
  const navigate = useNavigate()

  const isSuperAdmin = user?.role?.id === 1 || user?.role_users?.[0]?.role_id === 1
  const handleSelectClient = (client) => navigate(`/company-clients/${client.id}`)

  const [companies, setCompanies] = useState([])
  const [selectedCompanyId, setSelectedCompanyId] = useState(null)
  const [loadingCompanies, setLoadingCompanies] = useState(false)

  const selectedCompany = useMemo(
    () => companies.find(c => c.id === selectedCompanyId) || null,
    [companies, selectedCompanyId]
  )

  const companyName = selectedCompany?.name || companyDetails?.name || companyDetails?.company_name || ''

  const [clients,     setClients]     = useState([])
  const [totalCount,  setTotalCount]  = useState(0)
  const [loading,     setLoading]     = useState(true)
  const [hasFetched,  setHasFetched]  = useState(false)
  const [error,       setError]       = useState(null)
  const [page,        setPage]        = useState(0)
  const [pageSize,    setPageSize]    = useState(20)
  const [searchTerm,  setSearchTerm]  = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [formError, setFormError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [consultants, setConsultants] = useState([])
  const [loadingConsultants, setLoadingConsultants] = useState(false)
  const [selectedConsultantId, setSelectedConsultantId] = useState('')
  const [formCompanyId, setFormCompanyId] = useState(null)
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    gender: 'male',
    birthdate: '',
    country: 'USA',
    mobile: '',
    address: '',
    aptsuite: '',
    city: '',
    state: '',
    postalcode: '',
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim())
      setPage(0)
    }, 300)
    return () => clearTimeout(handler)
  }, [searchTerm])

  // Super admin: load all companies and default-select the first one
  useEffect(() => {
    if (!isSuperAdmin || !companyUrl) return
    let cancelled = false
    setLoadingCompanies(true)
    apiService.fetchCompanies(companyUrl).then(result => {
      if (cancelled) return
      if (result.success) {
        const list = Array.isArray(result.data) ? result.data : []
        setCompanies(list)
        if (list.length > 0) setSelectedCompanyId(list[0].id)
      }
      setLoadingCompanies(false)
    })
    return () => { cancelled = true }
  }, [isSuperAdmin, companyUrl])

  const loadClients = useCallback(
    async (signal) => {
      if (!companyUrl) return
      if (isSuperAdmin && !selectedCompanyId) return
      setLoading(true)
      setError(null)
      try {
        const payload = {
          fromIndex:   page * pageSize,
          limit:       pageSize,
          orderBy:     'DESC',
          orderByOn:   'id',
          searchtext:  debouncedSearch,
        }
        if (isSuperAdmin && selectedCompanyId) {
          payload.company_id = selectedCompanyId
        }
        const result = await apiService.post(`/${companyUrl}/getclients`, payload, signal)
        if (result.success) {
          setClients(result.data?.rows || [])
          setTotalCount(result.data?.count || 0)
        } else if (/no\s+(client|user)/i.test(result.error || '')) {
          // API reports "no clients/users found" as an error — treat as an empty list, not a failure
          setClients([])
          setTotalCount(0)
        } else {
          setError(result.error || 'Failed to load users')
          setClients([])
          setTotalCount(0)
        }
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Failed to load users')
        setClients([])
        setTotalCount(0)
      } finally {
        setLoading(false)
        if (!signal?.aborted) setHasFetched(true)
      }
    },
    [companyUrl, page, pageSize, debouncedSearch, isSuperAdmin, selectedCompanyId]
  )

  useEffect(() => {
    const controller = new AbortController()
    loadClients(controller.signal)
    return () => controller.abort()
  }, [loadClients])

  const handleCompanyChange = (value) => {
    setSelectedCompanyId(Number(value))
    setPage(0)
    setClients([])
    setTotalCount(0)
    setHasFetched(false)
  }

  useEffect(() => {
    if (isAddModalOpen && companyUrl && states.length === 0 && !isLoadingStates) {
      fetchStates(companyUrl)
    }
  }, [isAddModalOpen, companyUrl, fetchStates, states.length, isLoadingStates])

  // Super admin: when the dialog opens, default the form's company to the page's selected company
  useEffect(() => {
    if (isAddModalOpen && isSuperAdmin && selectedCompanyId && formCompanyId == null) {
      setFormCompanyId(selectedCompanyId)
    }
  }, [isAddModalOpen, isSuperAdmin, selectedCompanyId, formCompanyId])

  // Super admin: load consultants for the company selected in the form so the new user can be assigned to one
  useEffect(() => {
    if (!isAddModalOpen || !isSuperAdmin || !companyUrl || !formCompanyId) return
    let cancelled = false
    setLoadingConsultants(true)
    setConsultants([])
    setSelectedConsultantId('')
    apiService.post(`/${companyUrl}/companyadmin/getconsultants`, {
      fromIndex: 0,
      limit: 50,
      orderBy: 'DESC',
      orderByOn: 'id',
      searchtext: '',
      companyId: formCompanyId,
      company_id: formCompanyId,
    }).then(result => {
      if (cancelled) return
      if (result.success) {
        setConsultants(result.data?.rows || [])
      }
      setLoadingConsultants(false)
    })
    return () => { cancelled = true }
  }, [isAddModalOpen, isSuperAdmin, companyUrl, formCompanyId])

  const formatBirthdate = (value) => {
    if (!value) return ''
    const parts = value.split('-')
    if (parts.length !== 3) return value
    return `${parts[1]}/${parts[2]}/${parts[0]}`
  }

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const resetForm = () => {
    setFormData({
      firstname: '',
      lastname: '',
      email: '',
      gender: 'male',
      birthdate: '',
      country: 'USA',
      mobile: '',
      address: '',
      aptsuite: '',
      city: '',
      state: '',
      postalcode: '',
    })
  }

  const handleAddUserSubmit = async (e) => {
    e.preventDefault()
    setFormError(null)

    if (!formData.email || !formData.firstname || !formData.lastname || !formData.birthdate || !formData.state) {
      setFormError('Please fill all required fields.')
      return
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailPattern.test(formData.email)) {
      setFormError('Please enter a valid email.')
      return
    }

    if (!companyUrl) {
      setFormError('Missing company context.')
      return
    }

    if (isSuperAdmin && !formCompanyId) {
      setFormError('Please select a company first.')
      return
    }

    if (isSuperAdmin && !selectedConsultantId) {
      setFormError('Please select a consultant for this user.')
      return
    }

    setIsSubmitting(true)

    try {
      const createResult = await apiService.post(`/${companyUrl}/createclient`, {
        email: formData.email,
      })

      if (!createResult.success) {
        throw new Error(createResult.error || 'Failed to create user')
      }

      const createdId =
        createResult.data?.user_id ||
        createResult.data?.id ||
        createResult.data?.user?.id ||
        createResult.data?.client_id

      if (!createdId) {
        throw new Error('User created but id was not returned')
      }

      const updateResult = await apiService.put(`/${companyUrl}/updateclient`, {
        email: formData.email,
        gender: formData.gender === 'female' ? 'f' : 'm',
        firstname: formData.firstname,
        lastname: formData.lastname,
        birthdate: formatBirthdate(formData.birthdate),
        mobilephone: formData.mobile,
        address: formData.address,
        aptsuite: formData.aptsuite,
        city: formData.city,
        state: formData.state,
        postalcode: formData.postalcode,
        user_id: createdId,
        ...(isSuperAdmin ? { consultant_id: selectedConsultantId } : {}),
      })

      if (!updateResult.success) {
        throw new Error(updateResult.error || 'Failed to update user')
      }

      setIsAddModalOpen(false)
      resetForm()
      setSelectedConsultantId('')
      setFormCompanyId(null)
      setPage(0)
      await loadClients(new AbortController().signal)
      toast({ title: 'User created', description: `${formData.firstname} ${formData.lastname} was added successfully.` })
    } catch (err) {
      setFormError(err?.message || 'Failed to save user')
    } finally {
      setIsSubmitting(false)
    }
  }

  const totalPages  = Math.max(Math.ceil(totalCount / pageSize), 1)
  const rangeStart  = totalCount === 0 ? 0 : page * pageSize + 1
  const rangeEnd    = Math.min(totalCount, page * pageSize + clients.length)
  const handlePerPageChange = (e) => {
    setPageSize(Number(e.target.value))
    setPage(0)
  }

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <PageHeader
        eyebrow="Admin Panel"
        title="Company Users"
        subtitle="Manage and monitor all users across your organization"
      />

      {/* Toolbar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center lg:flex-1">
          {isSuperAdmin && (
            <div className="w-full sm:w-64 sm:flex-shrink-0">
              <Select
                value={selectedCompanyId != null ? String(selectedCompanyId) : ''}
                onValueChange={handleCompanyChange}
                disabled={loadingCompanies || companies.length === 0}
              >
                <SelectTrigger className="h-9 rounded-lg text-sm">
                  <div className="flex items-center gap-2 truncate">
                    <Building2 className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    <SelectValue placeholder={loadingCompanies ? 'Loading companies…' : 'Select a company'} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={String(company.id)}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="relative w-full sm:max-w-xs sm:flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full h-9 pl-9 pr-4 text-sm bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-fitness-primary/20 focus:border-fitness-primary-light transition-colors"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 justify-end">

          <Dialog
            open={isAddModalOpen}
            onOpenChange={(open) => {
              setIsAddModalOpen(open)
              if (!open) {
                resetForm()
                setSelectedConsultantId('')
                setFormCompanyId(null)
              }
            }}
          >
            <DialogTrigger asChild>
              <Button size="sm" className="h-9 px-4 text-sm bg-fitness-primary hover:bg-fitness-primary/90 text-primary-foreground gap-2">
                <Plus className="h-3.5 w-3.5" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col gap-0 overflow-hidden p-0">
              <DialogHeader className="shrink-0 border-b border-slate-100 px-6 py-4 pr-12">
                <DialogTitle>Add User</DialogTitle>
              </DialogHeader>
              <form className="flex flex-1 flex-col overflow-hidden" onSubmit={handleAddUserSubmit}>
              <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
                {isSuperAdmin && (
                  <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Assign To</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField label="Company" required>
                        <select
                          className={selectFieldClass}
                          value={formCompanyId != null ? String(formCompanyId) : ''}
                          onChange={(e) => setFormCompanyId(Number(e.target.value))}
                          disabled={loadingCompanies || companies.length === 0}
                          required
                        >
                          <option value="" disabled>
                            {loadingCompanies ? 'Loading companies...' : 'Select company'}
                          </option>
                          {companies.map((company) => (
                            <option key={company.id} value={company.id}>
                              {company.name}
                            </option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Consultant" required>
                        <select
                          className={selectFieldClass}
                          value={selectedConsultantId}
                          onChange={(e) => setSelectedConsultantId(e.target.value)}
                          disabled={loadingConsultants || consultants.length === 0}
                          required
                        >
                          <option value="" disabled>
                            {loadingConsultants
                              ? 'Loading consultants...'
                              : consultants.length === 0
                                ? 'No consultants available'
                                : 'Select consultant'}
                          </option>
                          {consultants.map((consultant) => (
                            <option key={consultant.id} value={consultant.id}>
                              {[consultant.firstname, consultant.lastname].filter(Boolean).join(' ') || consultant.email}
                            </option>
                          ))}
                        </select>
                      </FormField>
                    </div>
                    {!loadingConsultants && formCompanyId && consultants.length === 0 && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>No consultants found for this company. Add a consultant to this company before creating a user.</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Personal Information</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField label="First Name" required>
                      <Input
                        placeholder="e.g. John"
                        value={formData.firstname}
                        onChange={(e) => handleFormChange('firstname', e.target.value)}
                        required
                      />
                    </FormField>
                    <FormField label="Last Name" required>
                      <Input
                        placeholder="e.g. Doe"
                        value={formData.lastname}
                        onChange={(e) => handleFormChange('lastname', e.target.value)}
                        required
                      />
                    </FormField>
                    <FormField label="Email" required>
                      <Input
                        placeholder="name@example.com"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleFormChange('email', e.target.value)}
                        required
                      />
                    </FormField>
                    <FormField label="Birth Date" required>
                      <Input
                        type="date"
                        value={formData.birthdate}
                        onChange={(e) => handleFormChange('birthdate', e.target.value)}
                        required
                      />
                    </FormField>
                    <FormField label="Gender" required className="sm:col-span-2">
                      <div className="flex items-center gap-6 rounded-xl border border-input px-4 h-11">
                        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input
                            type="radio"
                            name="gender"
                            value="male"
                            checked={formData.gender === 'male'}
                            onChange={(e) => handleFormChange('gender', e.target.value)}
                          />
                          <span>Male</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input
                            type="radio"
                            name="gender"
                            value="female"
                            checked={formData.gender === 'female'}
                            onChange={(e) => handleFormChange('gender', e.target.value)}
                          />
                          <span>Female</span>
                        </label>
                      </div>
                    </FormField>
                    <FormField label="Country">
                      <select
                        className={selectFieldClass}
                        value={formData.country}
                        onChange={(e) => handleFormChange('country', e.target.value)}
                      >
                        <option value="USA">USA</option>
                      </select>
                    </FormField>
                    <FormField label="Mobile Phone">
                      <Input
                        placeholder="(000) 000-0000"
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) => handleFormChange('mobile', e.target.value)}
                      />
                    </FormField>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Address</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField label="Address" className="sm:col-span-2">
                      <Input
                        placeholder="Street address"
                        value={formData.address}
                        onChange={(e) => handleFormChange('address', e.target.value)}
                      />
                    </FormField>
                    <FormField label="Apt / Suite">
                      <Input
                        placeholder="Apt, suite, etc. (optional)"
                        value={formData.aptsuite}
                        onChange={(e) => handleFormChange('aptsuite', e.target.value)}
                      />
                    </FormField>
                    <FormField label="City">
                      <Input
                        placeholder="City"
                        value={formData.city}
                        onChange={(e) => handleFormChange('city', e.target.value)}
                      />
                    </FormField>
                    <FormField label="State" required>
                      <select
                        className={selectFieldClass}
                        value={formData.state}
                        onChange={(e) => handleFormChange('state', e.target.value)}
                        required
                      >
                        <option value="" disabled>
                          {isLoadingStates ? 'Loading states...' : 'Select state'}
                        </option>
                        {states.map((state) => (
                          <option key={state.stateCode} value={state.stateCode}>
                            {state.stateName}
                          </option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Postal Code">
                      <Input
                        placeholder="ZIP / Postal code"
                        value={formData.postalcode}
                        onChange={(e) => handleFormChange('postalcode', e.target.value)}
                      />
                    </FormField>
                  </div>
                </div>

                {formError && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{formError}</span>
                  </div>
                )}
              </div>

              <DialogFooter className="shrink-0 gap-2 border-t border-slate-100 px-6 py-4 sm:gap-2">
                <Button
                  variant="outline"
                  type="button"
                  className="h-10 px-5"
                  onClick={() => {
                    setIsAddModalOpen(false)
                    resetForm()
                    setSelectedConsultantId('')
                    setFormCompanyId(null)
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="h-10 px-5 bg-fitness-primary hover:bg-fitness-primary/90"
                  disabled={isSubmitting || (isSuperAdmin && consultants.length === 0)}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* User Directory */}
      <Card className="border border-gray-100 shadow-sm overflow-hidden">
        <CardHeader className="px-6 py-4 border-b border-gray-100 bg-white">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-fitness-primary" />
              <CardTitle className="text-base font-semibold text-gray-900">User Directory</CardTitle>
            </div>
            <span className="text-xs font-medium text-gray-500">
              {loading && !hasFetched ? 'Loading…' : `${totalCount.toLocaleString()} total ${totalCount === 1 ? 'user' : 'users'}`}
            </span>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Loading */}
          {loading && !hasFetched && (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading users…</span>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-red-500">
              <AlertCircle className="h-6 w-6" />
              <p className="text-sm">{error}</p>
              <Button variant="outline" size="sm" onClick={() => loadClients(new AbortController().signal)}>
                Retry
              </Button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && hasFetched && clients.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-gray-400">
              <Users className="h-8 w-8" />
              <p className="text-sm font-medium text-gray-500">
                {debouncedSearch
                  ? `No users match "${debouncedSearch}"`
                  : companyName
                    ? `No users found for ${companyName}`
                    : 'No users found'}
              </p>
              {debouncedSearch && (
                <p className="text-xs text-gray-400">Try a different search term</p>
              )}
            </div>
          )}

          {/* Rows */}
          {clients.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date of Birth</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {clients.map((client) => {
                    const name = getUserName(client)
                    const isActive = client.enabled === 1
                    return (
                      <tr
                        key={client.id}
                        className="cursor-pointer transition-colors hover:bg-fitness-primary/10"
                        onClick={() => handleSelectClient(client)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <UserAvatar name={name} email={client.email} className="h-9 w-9 flex-shrink-0" />
                            <span className="truncate text-sm font-semibold text-gray-900" title={name}>
                              {name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <span className="flex max-w-[200px] items-center gap-1.5 truncate" title={client.email}>
                            <Mail className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                            {client.email || 'Not set'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{getMobile(client)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{getLocation(client)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{getBirthDate(client)}</td>
                        <td className="px-4 py-3">
                          <RoleBadge client={client} />
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge active={isActive} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {hasFetched && !error && (
            <div className="flex items-center justify-between px-6 py-3.5 border-t border-gray-100 bg-gray-50/60 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Rows per page:</label>
                <select
                  value={pageSize}
                  onChange={handlePerPageChange}
                  className="h-8 px-2 pr-6 text-xs border border-gray-200 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-fitness-primary/20 focus:border-fitness-primary-light appearance-none cursor-pointer"
                  style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%236b7280' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                >
                  {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3">
                <p className="text-xs text-gray-500">
                  <span className="font-medium text-gray-700">{rangeStart}–{rangeEnd}</span>{' '}
                  of <span className="font-medium text-gray-700">{totalCount}</span> users
                </p>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline" size="sm"
                    disabled={loading || page === 0}
                    onClick={() => setPage(p => p - 1)}
                    className="h-8 px-3 text-xs border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                  >
                    Previous
                  </Button>
                  <span className="text-xs text-gray-500 tabular-nums">{page + 1} / {totalPages}</span>
                  <Button
                    variant="outline" size="sm"
                    disabled={loading || page + 1 >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                    className="h-8 px-3 text-xs border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default CompanyClients
